import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {VisitContext} from "../../../../visit-context";
import {AppContext} from "../../../../../app-context";
import {
  DeclarationStatus,
  DownloadableFile,
  HarbourDuesDeclaration,
  TaskMessageStatus,
  Visit
} from "@portbase/bezoekschip-service-typescriptmodels";
import {PortvisitUtils} from "../../../../../refdata/portvisit-utils";
import moment from "moment-timezone";
import {sendQuery} from "../../../../../common/utils";
import {HarbourDuesDeclarationTab} from "../../../types";
import {NewPortvisitUtils} from "../../../../../new-portvisit/new-portvisit.utils";
import {HarbourDuesPermissionService} from "../../../services/harbour-dues-permission.service";
import {HarbourDuesService} from "../../../services/harbour-dues.service";

@Component({
  selector: 'app-harbour-dues-info',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InfoComponent {

  protected readonly PortvisitUtils = PortvisitUtils;
  protected readonly context = VisitContext;
  protected readonly appContext = AppContext;
  readonly isAvailable = this.permissionService.isAvailable();
  readonly hasArrived = this.permissionService.hasArrived();

  selectedDeclaration = input<HarbourDuesDeclaration>();
  visit = input<Visit>();
  declarationTabs = input<HarbourDuesDeclarationTab[]>();
  readonly declarationSelected = computed(() => !!this.selectedDeclaration())
  readonly port = computed(() => this.harbourDuesService.getPort());
  readonly tariffLink = computed(() => this.getTariffLink(this.port()));

  readonly declarationStatus = computed<DeclarationStatus | undefined>(() =>
    this.selectedDeclaration()?.status
  );

  readonly accepted = computed(() =>
    this.declarationStatus() === DeclarationStatus.ACCEPTED
  );

  readonly declared = computed(() =>
    this.declarationStatus() === DeclarationStatus.DECLARED
  );

  readonly rejectionReason = computed(() =>
    this.selectedDeclaration()?.rejectionReason
  );

  readonly declarationSent = computed(() => {
    const s = this.declarationStatus();
    return !!s && [
      DeclarationStatus.DECLARED,
      DeclarationStatus.ACCEPTED,
      DeclarationStatus.REJECTED
    ].includes(s);
  });

  readonly declarationPeriod = computed(() => {
    const decl = this.selectedDeclaration();
    const tabs = this.declarationTabs();

    if (decl && tabs.length > 1) {
      return this.invoicePeriod(decl.invoiceStart, decl.invoiceEnd);
    }
    return '';
  });

  constructor(
    private permissionService: HarbourDuesPermissionService,
    private harbourDuesService: HarbourDuesService) {
  }

  private getTariffLink(port: string): string {
    switch (port) {
      case 'Amsterdam':
        return 'https://www.portofamsterdam.com/en/shipping/sea-shipping/harbour-dues';
      case 'Rotterdam':
        return 'https://www.portofrotterdam.com/sites/default/files/2025-12/port-tariffs-and-conditions-port-of%20-rotterdam-2026.pdf';
      default:
        return '';
    }
  }

  private invoicePeriod(start: string, end: string) {
    const invoiceStart = moment(start).format('D MMM');
    const invoiceEnd = end ? moment(end).format('D MMM') : 'now';
    if (invoiceStart === invoiceEnd) {
      return invoiceStart;
    }
    return `${invoiceStart} - ${invoiceEnd}`.toLowerCase();
  }

  downloadPdf() {
    const decl = this.selectedDeclaration();
    if (!decl) return;

    sendQuery(
      'com.portbase.bezoekschip.common.api.harbourdues.queries.GetHarbourDuesDeclarationPdf',
      {crn: decl.crn, statementNumber: decl.statementNumber},
      {caching: false, showSpinner: true}
    ).subscribe((file: DownloadableFile) => {
      const a = document.createElement('a');
      a.href = `data:${file.mediaType};base64,${file.data}`;
      a.setAttribute('download', file.fileName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return new Promise(resolve => setTimeout(() => resolve(file), 100));
    });
  }

  declarationTaskMessageStatus() {
    const decl = this.selectedDeclaration();
    if (decl?.status) {
      return NewPortvisitUtils.declarationStatusToTaskMessageStatus(decl.status);
    }
    return TaskMessageStatus.UNKNOWN;
  }

  goToCosts(): void {
    document.getElementById('costs')?.scrollIntoView();
  }
}
